// src/hooks/useIsPaidUser.js
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

const API_URL   = process.env.REACT_APP_API_URL;         // e.g. https://.../prod
const AUDIENCE  = process.env.REACT_APP_AUTH0_AUDIENCE;  // e.g. https://.../api
const API_SCOPE = "read:is-paid";

// tiny helper: decode JWT header safely (dev logging only)
function decodeHeader(token) {
  try {
    const h = token.split(".")[0];
    const json = atob(h.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) || {};
  } catch {
    return {};
  }
}

export function useIsPaidUser(course = "personal-finance") {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

  const [isPaid, setIsPaid]     = useState(false);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();

    async function run() {
      setLoading(true);
      setError("");
      setIsPaid(false);

      // wait for Auth0 init + env sanity
      if (isLoading) return;
      if (!API_URL || !AUDIENCE) {
        setError("Missing REACT_APP_API_URL or REACT_APP_AUTH0_AUDIENCE");
        setLoading(false);
        return;
      }
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        // >>> THIS is the important part <<<:
        // Ask for an ACCESS TOKEN (JWS, RS256) for YOUR API audience + scope.
        // detailedResponse:true ensures we get the access_token explicitly.
        const { access_token } = await getAccessTokenSilently({
          authorizationParams: {
            audience: AUDIENCE,
            scope: API_SCOPE,
          },
          detailedResponse: true,
          cacheMode: "off", // force fresh after toggling encryption settings in Auth0
        });

        // Dev sanity checks: should be 3 parts and no "enc" in header
        const parts = (access_token || "").split(".").length;
        if (parts !== 3) {
          throw new Error(`Wrong token format (got ${parts} parts). Need a 3-part JWS access token.`);
        }
        const hdr = decodeHeader(access_token);
        if (hdr.enc || hdr.alg !== "RS256") {
          throw new Error("Need RS256 ACCESS token (JWS). Looks like an encrypted ID token (JWE) instead.");
        }

        const url = `${API_URL}/is-paid?course=${encodeURIComponent(course)}`;
        const res = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Bearer ${access_token}` },
          signal: ac.signal,
        });

        if (!res.ok) {
          const body = await res.text().catch(() => "");
          throw new Error(`is-paid HTTP ${res.status}${body ? ` — ${body}` : ""}`);
        }

        const data = await res.json().catch(() => ({}));
        if (!cancelled) setIsPaid(!!data.isPaid);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to check entitlement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => { cancelled = true; ac.abort(); };
  }, [isAuthenticated, isLoading, getAccessTokenSilently, course]);

  return { isPaidUser: isPaid, isCheckingPaid: loading, error };
}
